import { useMemo, useState } from "react";
import { useInvoicePickerQuery } from "@api/invoice.api";
import { getInvoiceScheduleBlockReason } from "@common/helpers/domains/accounting/invoiceScheduleEligibility";
import { Invoice, InvoiceStatus } from "@common/types/invoice";
import type { PickerStatus } from "@design-system/Picker/Picker.types";
import { SEARCH_DEBOUNCE_TIME } from "@constants";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import { toPickerLoadMore } from "@helpers/pickerLoadMore";

const APPLICABLE_INVOICE_STATUSES = [InvoiceStatus.OPEN, InvoiceStatus.PAID];

const NO_INVOICES: Invoice[] = [];

/**
 * Open or paid invoices of the entity that a schedule can apply to, searched
 * on the server and paged 50 at a time. Blocked invoices are dropped here, so
 * a page can come back shorter than it was fetched.
 */
export function useScheduleApplySources({
    entityId,
}: {
    entityId: number | null;
}) {
    const [typedSearch, setTypedSearch] = useState("");
    const [search, setSearch] = useState("");
    useDebouncedEffect(
        () => {
            setSearch(typedSearch);
        },
        [typedSearch],
        SEARCH_DEBOUNCE_TIME,
    );

    const invoicesQuery = useInvoicePickerQuery(
        {
            filters: {
                entityIds: entityId == null ? [] : [entityId],
                invoiceStatuses: APPLICABLE_INVOICE_STATUSES,
                search: search || undefined,
            },
        },
        { enabled: entityId != null },
    );
    const fetchedInvoices = invoicesQuery.data ?? NO_INVOICES;
    const invoices = useMemo(
        () =>
            fetchedInvoices.filter(
                (invoice) => getInvoiceScheduleBlockReason(invoice) === null,
            ),
        [fetchedInvoices],
    );

    return {
        invoices,
        invoicesStatus: getInvoicesStatus(invoicesQuery),
        invoicesLoadMore: toPickerLoadMore(invoicesQuery),
        setInvoiceSearch: setTypedSearch,
    };
}

const getInvoicesStatus = ({
    isLoading,
    isLoadingError,
}: {
    isLoading: boolean;
    isLoadingError: boolean;
}): PickerStatus => {
    if (isLoading) {
        return "loading";
    }
    return isLoadingError ? "error" : "idle";
};
